import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLOBAL_ENV_DEFAULT_PARAMS } from '../../performance/schema.js';

export const CA_HIFI_CORE_SCENE_ID = 'ca_hifi_core';

/** Defaults aligned with raw-scenes/ca-hifi-core.html (Bokeh / standalone HTML removed). */
const DEFAULTS = {
  GRID: 20,
  GEN_INTERVAL: 4,
  LERP_FRAMES: 8,
  SEED_DENSITY: 0.7,
  IGNITE: 3.0,
  STABILISE: 4.0,
  DYING_DRAG: 2.0,
  CONTAGION: 4.0,
  GROWING_KILL: 6.0,
  DECAY_RATE: 0.012,
  RESURRECT: 0.08,
  RESURRECT_FLOOD: 4.0,
  SCALE_NEWBORN: 0.45,
  SCALE_GROWING: 0.72,
  SCALE_STABLE: 1.0,
  SCALE_DYING: 0.55,
  SCALE_JITTER: 0.08,
  RESEED_THRESH: 120,
  RESEED_DENSITY: 0.5,
  RESEED_COOL: 80,
  ORBIT_MIN: 18,
  ORBIT_MAX: 120,
};

/** Global env orbit defaults are marching-scale (500 / 5000); map into CA world distances. */
const GLOBAL_ORBIT_REF_MIN = 500;
const GLOBAL_ORBIT_REF_MAX = 5000;

/** Map small dir-light position sliders (~±3) into CA space (~tens of units). */
const CA_DIR_LIGHT_POS_SCALE = 40;
/** Map marching-scale point positions (e.g. z=100) into CA grid scale (~20). */
const CA_POINT_LIGHT_POS_SCALE = 0.22;

export const sceneMeta = {
  id: CA_HIFI_CORE_SCENE_ID,
  label: 'CA hi-fi core',
  defaultBindings: {},
  paramSpecs: {
    genInterval: { min: 1, max: 12, step: 1, label: 'CA step every N frames' },
  },
  defaultParams: {
    genInterval: DEFAULTS.GEN_INTERVAL,
  },
  bindingTargets: [
    { key: 'bloom', label: 'Bloom', min: 0, max: 1 },
    { key: 'caSpeed', label: 'CA speed', min: 0, max: 1 },
  ],
};

const S_DEAD = 0.0;
const S_NEWBORN = 0.25;
const S_GROWING = 0.5;
const S_STABLE = 0.75;
const S_DYING = 1.0;

const CA_VERT = /* glsl */ `
out vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const CA_FRAG = /* glsl */ `
precision highp float;
in vec2 vUv;
out vec4 fragColor;

uniform sampler2D uState;
uniform float uGrid;
uniform float uTime;
uniform float uIgnite;
uniform float uStabilise;
uniform float uDyingDrag;
uniform float uContagion;
uniform float uGrowingKill;
uniform float uDecay;
uniform float uResurrect;
uniform float uResurrectFlood;

#define S_DEAD_V    0.00
#define S_NEWBORN_V 0.25
#define S_GROWING_V 0.50
#define S_STABLE_V  0.75
#define S_DYING_V   1.00
#define EPS       0.12

vec2 getCell(float x, float y, float z) {
  float G = uGrid;
  x = mod(x+G,G); y = mod(y+G,G); z = mod(z+G,G);
  vec2 uv = (vec2(x, y*G+z) + 0.5) / vec2(G, G*G);
  vec4 t = texture(uState, uv);
  return vec2(t.r, t.g);
}

bool isState(float s, float target) { return abs(s - target) < EPS; }
float hash(vec3 p, float t) { return fract(sin(dot(p+t, vec3(127.1,311.7,74.7))) * 43758.5453); }

void main() {
  float G = uGrid;
  float px = floor(vUv.x * G);
  float py = floor(vUv.y * G * G);
  float cx = px, cy = floor(py/G), cz = mod(py,G);

  vec2  cell     = getCell(cx, cy, cz);
  float state    = cell.x;
  float dyingAge = cell.y;

  float newbornAll = 0.0, newbornAboveOnly = 0.0;
  float growingOrStable = 0.0, dyingAll = 0.0, dyingBelow = 0.0, totalAlive = 0.0;

  for (float dx=-1.0; dx<=1.0; dx+=1.0)
  for (float dy=-1.0; dy<=1.0; dy+=1.0)
  for (float dz=-1.0; dz<=1.0; dz+=1.0) {
    if (dx==0.0 && dy==0.0 && dz==0.0) continue;
    vec2  nb = getCell(cx+dx, cy+dy, cz+dz);
    float ns = nb.x;
    if (ns > EPS) totalAlive += 1.0;
    if (isState(ns, S_NEWBORN_V)) {
      newbornAll += 1.0;
      if (dy > 0.5) newbornAboveOnly += 1.0;
    }
    if (isState(ns, S_GROWING_V) || isState(ns, S_STABLE_V)) growingOrStable += 1.0;
    if (isState(ns, S_DYING_V)) {
      dyingAll += 1.0;
      if (dy < -0.5) dyingBelow += 1.0;
    }
  }

  float nextState    = state;
  float nextDyingAge = 0.0;
  float rng = hash(vec3(cx, cy, cz), uTime);

  if (isState(state, S_DEAD_V)) {
    if (newbornAll >= uIgnite) nextState = S_NEWBORN_V;
  }
  else if (isState(state, S_NEWBORN_V)) {
    if (dyingBelow >= uDyingDrag) nextState = S_DEAD_V;
    else nextState = S_GROWING_V;
  }
  else if (isState(state, S_GROWING_V)) {
    if      (dyingAll >= uGrowingKill)      { nextState = S_DYING_V; nextDyingAge = 0.0; }
    else if (growingOrStable >= uStabilise)   nextState = S_STABLE_V;
  }
  else if (isState(state, S_STABLE_V)) {
    if (dyingAll >= uContagion || totalAlive < 2.0 || rng < uDecay)
      { nextState = S_DYING_V; nextDyingAge = 0.0; }
  }
  else if (isState(state, S_DYING_V)) {
    if (newbornAboveOnly >= uResurrectFlood && rng < uResurrect) nextState = S_GROWING_V;
    else if (dyingAge > 0.45) nextState = S_DEAD_V;
    else { nextState = S_DYING_V; nextDyingAge = 0.5; }
  }

  fragColor = vec4(nextState, nextDyingAge, 0.0, 1.0);
}
`;

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

function parseColorHex(hex) {
  const s = String(hex || '#ffffff').replace('#', '');
  const n = parseInt(s.length === 3 ? [...s].map((c) => c + c).join('') : s, 16);
  return Number.isFinite(n) ? n : 0xffffff;
}

function mapBind01(resolved, key, min, max, manual) {
  if (resolved[key] === undefined) return manual;
  return min + clamp01(resolved[key]) * (max - min);
}

/**
 * GPU CA on a float render target + instanced cubes; bloom is applied in ThreeRenderer.
 */
export class CAHiFiCoreScene {
  constructor() {
    this.threeScene = new THREE.Scene();
    this.threeScene.background = new THREE.Color(0x000305);
    this.threeScene.fog = new THREE.FogExp2(0x000305, 0.022);

    this._params = {
      ...GLOBAL_ENV_DEFAULT_PARAMS,
      ...sceneMeta.defaultParams,
    };

    this._ambientLight = null;
    this._directionalLight = null;
    this._pointLight = null;
    this._gl = null;
    this._camera = null;
    this._controls = null;
    this._canvas = null;

    this._inited = false;
    this._frame = 0;
    this._gen = 0;

    this._displayScales = null;
    this._targetScales = null;
    this._jitterSeeds = null;

    this._grid = DEFAULTS.GRID;
    this._texW = 0;
    this._texH = 0;
    this._rtA = null;
    this._rtB = null;
    this._pixBuf = null;

    this._meshNewborn = null;
    this._meshGrowing = null;
    this._meshStable = null;
    this._meshDying = null;
    this._sharedBoxGeo = null;

    this._caScene = null;
    this._caQuad = null;
    this._caCam = null;

    this._dummy = new THREE.Object3D();
    this._reseedCooldown = DEFAULTS.RESEED_COOL;

    this._materials = null;

    this._lastDt = 0;
  }

  getMidiTargets() {
    return sceneMeta.bindingTargets;
  }

  getSceneLabel() {
    return sceneMeta.label;
  }

  getCamera() {
    return this._camera;
  }

  /**
   * @param {Record<string, unknown>} params
   */
  applyParams(params = {}) {
    this._params = { ...GLOBAL_ENV_DEFAULT_PARAMS, ...sceneMeta.defaultParams, ...params };
    this._syncOrbitLimits();
    this._syncEnvFromParams();
  }

  _syncEnvFromParams() {
    if (!this._ambientLight || !this._directionalLight || !this._pointLight) return;

    const ai = Number(this._params.ambientIntensity);
    this._ambientLight.intensity = Number.isFinite(ai) ? ai : 3;
    this._ambientLight.color.setHex(parseColorHex(this._params.ambientColor));

    const di = Number(this._params.dirLightIntensity);
    this._directionalLight.intensity = Number.isFinite(di) ? di : 3;
    this._directionalLight.color.setHex(parseColorHex(this._params.dirLightColor));
    const ds = CA_DIR_LIGHT_POS_SCALE;
    this._directionalLight.position.set(
      (Number(this._params.dirLightPosX) || 0) * ds,
      (Number(this._params.dirLightPosY) || 0) * ds,
      (Number(this._params.dirLightPosZ) || 1) * ds
    );

    const pi = Number(this._params.pointLightIntensity);
    this._pointLight.intensity = Number.isFinite(pi) ? pi : 3;
    this._pointLight.color.setHex(parseColorHex(this._params.pointLightColor));
    const ps = CA_POINT_LIGHT_POS_SCALE;
    this._pointLight.position.set(
      (Number(this._params.pointLightPosX) || 0) * ps,
      (Number(this._params.pointLightPosY) || 0) * ps,
      (Number(this._params.pointLightPosZ) || 100) * ps
    );

    const bg = parseColorHex(this._params.backgroundColor);
    if (this.threeScene.background instanceof THREE.Color) {
      this.threeScene.background.setHex(bg);
    } else {
      this.threeScene.background = new THREE.Color(bg);
    }
    if (this.threeScene.fog instanceof THREE.FogExp2) {
      this.threeScene.fog.color.setHex(bg);
    }

    if (this._camera) {
      const fov = Number(this._params.cameraFov);
      if (Number.isFinite(fov)) {
        this._camera.fov = THREE.MathUtils.clamp(fov, 10, 120);
        this._camera.updateProjectionMatrix();
      }
    }
  }

  _syncOrbitLimits() {
    if (!this._controls) return;
    const gMin = Number(this._params.orbitMinDistance);
    const gMax = Number(this._params.orbitMaxDistance);
    if (Number.isFinite(gMin) && Number.isFinite(gMax) && gMax > gMin + 1) {
      const lo = Math.max(1, gMin * (DEFAULTS.ORBIT_MIN / GLOBAL_ORBIT_REF_MIN));
      const hi = Math.max(lo + 1, gMax * (DEFAULTS.ORBIT_MAX / GLOBAL_ORBIT_REF_MAX));
      this._controls.minDistance = lo;
      this._controls.maxDistance = hi;
    }
  }

  /**
   * @param {{
   *   getSize?: () => { w: number; h: number };
   *   canvas?: HTMLCanvasElement | null;
   *   getWebGLRenderer?: () => THREE.WebGLRenderer | null;
   * }} [ctx]
   */
  init(ctx) {
    if (this._inited) return;

    this._gl = typeof ctx?.getWebGLRenderer === 'function' ? ctx.getWebGLRenderer() : null;
    if (!this._gl) {
      console.warn('CAHiFiCoreScene: no WebGL renderer; scene will not render.');
      return;
    }
    this._inited = true;

    this._canvas = ctx?.canvas ?? document.getElementById('main-canvas');
    const { w, h } = ctx?.getSize?.() ?? {
      w: window.innerWidth,
      h: window.innerHeight,
    };

    this._camera = new THREE.PerspectiveCamera(52, w / Math.max(h, 1), 0.1, 300);
    this._camera.position.set(34, 26, 34);
    this._camera.lookAt(0, 0, 0);

    if (this._canvas) {
      this._controls = new OrbitControls(this._camera, this._canvas);
      this._controls.enableDamping = true;
      this._controls.dampingFactor = 0.05;
      this._controls.target.set(0, 0, 0);
    }
    this._syncOrbitLimits();

    this._buildLights();
    this._buildMaterials();

    this._caScene = new THREE.Scene();
    this._caQuad = new THREE.Mesh(
      new THREE.PlaneGeometry(2, 2),
      new THREE.ShaderMaterial({
        glslVersion: THREE.GLSL3,
        vertexShader: CA_VERT,
        fragmentShader: CA_FRAG,
        uniforms: {
          uState: { value: null },
          uGrid: { value: this._grid },
          uTime: { value: 0 },
          uIgnite: { value: DEFAULTS.IGNITE },
          uStabilise: { value: DEFAULTS.STABILISE },
          uDyingDrag: { value: DEFAULTS.DYING_DRAG },
          uContagion: { value: DEFAULTS.CONTAGION },
          uGrowingKill: { value: DEFAULTS.GROWING_KILL },
          uDecay: { value: DEFAULTS.DECAY_RATE },
          uResurrect: { value: DEFAULTS.RESURRECT },
          uResurrectFlood: { value: DEFAULTS.RESURRECT_FLOOD },
        },
      })
    );
    this._caScene.add(this._caQuad);
    this._caCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    this._buildGrid();

    this.applyParams(this._params);
  }

  _buildLights() {
    this._ambientLight = new THREE.AmbientLight(0xffffff, 1);
    this.threeScene.add(this._ambientLight);

    this._directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    this._directionalLight.castShadow = true;
    this._directionalLight.shadow.mapSize.set(2048, 2048);
    this._directionalLight.shadow.camera.near = 1;
    this._directionalLight.shadow.camera.far = 120;
    this._directionalLight.shadow.camera.left = this._directionalLight.shadow.camera.bottom = -30;
    this._directionalLight.shadow.camera.right = this._directionalLight.shadow.camera.top = 30;
    this._directionalLight.shadow.bias = -0.0005;
    this.threeScene.add(this._directionalLight);

    this._pointLight = new THREE.PointLight(0xffffff, 1, 0, 0);
    this.threeScene.add(this._pointLight);

    this._syncEnvFromParams();
  }

  _buildMaterials() {
    const matNewborn = new THREE.MeshPhysicalMaterial({
      color: 0x88ffcc,
      emissive: 0x22ff66,
      emissiveIntensity: 1.2,
      metalness: 0.0,
      roughness: 0.05,
      clearcoat: 1.0,
      clearcoatRoughness: 0.05,
      transparent: true,
      opacity: 0.72,
    });

    const matGrowing = new THREE.MeshPhysicalMaterial({
      color: 0xbbee44,
      emissive: 0x334400,
      emissiveIntensity: 0.4,
      metalness: 0.4,
      roughness: 0.2,
      clearcoat: 0.9,
      clearcoatRoughness: 0.08,
      transparent: true,
      opacity: 0.93,
    });
    matGrowing.onBeforeCompile = (shader) => {
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <opaque_fragment>',
        `
        vec3 vd = normalize(vViewPosition);
        float vdn = clamp(dot(normal, vd), 0.0, 1.0);
        vec3 iriA = vec3(0.2, 1.0, 0.4);
        vec3 iriB = vec3(0.9, 1.0, 0.1);
        vec3 iriC = vec3(0.1, 0.85, 1.0);
        vec3 iri = mix(iriA, iriB, smoothstep(0.0, 0.5, vdn));
        iri = mix(iri, iriC, smoothstep(0.45, 0.9, vdn));
        outgoingLight += iri * 0.3;
        #include <opaque_fragment>
        `
      );
    };
    matGrowing.needsUpdate = true;

    const matStable = new THREE.MeshPhysicalMaterial({
      color: 0xeeeeff,
      emissive: 0x0a0a18,
      emissiveIntensity: 0.08,
      metalness: 0.02,
      roughness: 0.55,
      clearcoat: 0.5,
      clearcoatRoughness: 0.25,
    });

    const matDying = new THREE.MeshPhysicalMaterial({
      color: 0xff4411,
      emissive: 0xff2200,
      emissiveIntensity: 2.0,
      metalness: 0.05,
      roughness: 0.45,
      clearcoat: 0.15,
      clearcoatRoughness: 0.6,
      transparent: true,
      opacity: 0.82,
    });

    this._materials = { matNewborn, matGrowing, matStable, matDying };
  }

  _initSmoothBuffers(G) {
    const n = G * G * G;
    this._displayScales = new Float32Array(n);
    this._targetScales = new Float32Array(n);
    this._jitterSeeds = new Float32Array(n);
    for (let i = 0; i < n; i++) this._jitterSeeds[i] = Math.random();
  }

  _makeRT() {
    return new THREE.WebGLRenderTarget(this._texW, this._texH, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      format: THREE.RGBAFormat,
      type: THREE.FloatType,
    });
  }

  _makeHollowCubeData() {
    const G = this._grid;
    const data = new Float32Array(this._texW * this._texH * 4);
    const cx = Math.floor(G / 2);
    const half = Math.floor(G / 3);
    for (let y = 0; y < G; y++)
      for (let z = 0; z < G; z++)
        for (let x = 0; x < G; x++) {
          const onShell =
            (x === cx - half ||
              x === cx + half ||
              y === cx - half ||
              y === cx + half ||
              z === cx - half ||
              z === cx + half) &&
            x >= cx - half &&
            x <= cx + half &&
            y >= cx - half &&
            y <= cx + half &&
            z >= cx - half &&
            z <= cx + half;
          const pi = (y * G + z) * this._texW + x;
          data[pi * 4] = onShell && Math.random() < DEFAULTS.SEED_DENSITY ? S_NEWBORN : S_DEAD;
          data[pi * 4 + 3] = 1.0;
        }
    return data;
  }

  /**
   * Upload float RGBA into render target; disposes temp GPU resources.
   */
  _uploadData(rt, data) {
    const tex = new THREE.DataTexture(data, this._texW, this._texH, THREE.RGBAFormat, THREE.FloatType);
    tex.needsUpdate = true;
    const mat = new THREE.MeshBasicMaterial({ map: tex });
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
    const s = new THREE.Scene();
    s.add(quad);
    const c = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this._gl.setRenderTarget(rt);
    this._gl.render(s, c);
    this._gl.setRenderTarget(null);
    tex.dispose();
    mat.dispose();
    quad.geometry.dispose();
  }

  _buildGrid() {
    if (!this._gl || !this._materials) return;

    const G = DEFAULTS.GRID;
    this._grid = G;
    this._texW = G;
    this._texH = G * G;

    if (this._rtA) {
      this._rtA.dispose();
      this._rtB.dispose();
    }
    this._rtA = this._makeRT();
    this._rtB = this._makeRT();
    this._pixBuf = new Float32Array(this._texW * this._texH * 4);
    this._initSmoothBuffers(G);

    for (const m of [this._meshNewborn, this._meshGrowing, this._meshStable, this._meshDying]) {
      if (m) this.threeScene.remove(m);
    }
    if (this._sharedBoxGeo) this._sharedBoxGeo.dispose();
    this._sharedBoxGeo = new THREE.BoxGeometry(0.86, 0.86, 0.86);

    const MAX = G * G * G;
    const { matNewborn, matGrowing, matStable, matDying } = this._materials;
    this._meshNewborn = new THREE.InstancedMesh(this._sharedBoxGeo, matNewborn, MAX);
    this._meshGrowing = new THREE.InstancedMesh(this._sharedBoxGeo, matGrowing, MAX);
    this._meshStable = new THREE.InstancedMesh(this._sharedBoxGeo, matStable, MAX);
    this._meshDying = new THREE.InstancedMesh(this._sharedBoxGeo, matDying, MAX);
    for (const m of [this._meshNewborn, this._meshGrowing, this._meshStable, this._meshDying]) {
      m.castShadow = true;
      m.receiveShadow = true;
      this.threeScene.add(m);
      m.count = 0;
    }

    if (this._caQuad) this._caQuad.material.uniforms.uGrid.value = G;
    this._uploadData(this._rtA, this._makeHollowCubeData());
    this._reseedCooldown = DEFAULTS.RESEED_COOL;
  }

  _computeStep(g) {
    const mat = this._caQuad.material;
    mat.uniforms.uState.value = this._rtA.texture;
    mat.uniforms.uTime.value = g * 0.1;
    this._gl.setRenderTarget(this._rtB);
    this._gl.render(this._caScene, this._caCam);
    this._gl.setRenderTarget(null);
    const t = this._rtA;
    this._rtA = this._rtB;
    this._rtB = t;
  }

  _mergeReseed() {
    this._gl.readRenderTargetPixels(this._rtA, 0, 0, this._texW, this._texH, this._pixBuf);
    const G = this._grid;
    const cx = Math.floor(G / 2);
    const half = Math.floor(G / 3);
    for (let y = 0; y < G; y++)
      for (let z = 0; z < G; z++)
        for (let x = 0; x < G; x++) {
          const onShell =
            (x === cx - half ||
              x === cx + half ||
              y === cx - half ||
              y === cx + half ||
              z === cx - half ||
              z === cx + half) &&
            x >= cx - half &&
            x <= cx + half &&
            y >= cx - half &&
            y <= cx + half &&
            z >= cx - half &&
            z <= cx + half;
          if (onShell && Math.random() < DEFAULTS.RESEED_DENSITY) {
            const pi = (y * G + z) * this._texW + x;
            if (this._pixBuf[pi * 4] < 0.1) this._pixBuf[pi * 4] = S_NEWBORN;
          }
        }
    this._uploadData(this._rtA, this._pixBuf);
    this._reseedCooldown = DEFAULTS.RESEED_COOL;
  }

  _targetScaleForState(s) {
    if (s < 0.1) return 0;
    if (Math.abs(s - S_NEWBORN) < 0.12) return DEFAULTS.SCALE_NEWBORN;
    if (Math.abs(s - S_GROWING) < 0.12) return DEFAULTS.SCALE_GROWING;
    if (Math.abs(s - S_STABLE) < 0.12) return DEFAULTS.SCALE_STABLE;
    if (Math.abs(s - S_DYING) < 0.12) return DEFAULTS.SCALE_DYING;
    return 0;
  }

  _readbackAndDisplay() {
    this._gl.readRenderTargetPixels(this._rtA, 0, 0, this._texW, this._texH, this._pixBuf);

    const G = this._grid;
    for (let y = 0; y < G; y++)
      for (let z = 0; z < G; z++)
        for (let x = 0; x < G; x++) {
          const ci = y * G * G + z * G + x;
          const pi = (y * G + z) * this._texW + x;
          const base = this._targetScaleForState(this._pixBuf[pi * 4]);
          const jit = base > 0 ? (this._jitterSeeds[ci] - 0.5) * 2 * DEFAULTS.SCALE_JITTER : 0;
          this._targetScales[ci] = Math.max(0, base + jit);
        }

    const lerpT = 1.0 / Math.max(1, DEFAULTS.LERP_FRAMES);
    let cN = 0;
    let cG = 0;
    let cS = 0;
    let cD = 0;
    let live = 0;
    const off = G / 2;
    const dummy = this._dummy;

    for (let y = 0; y < G; y++)
      for (let z = 0; z < G; z++)
        for (let x = 0; x < G; x++) {
          const ci = y * G * G + z * G + x;
          const pi = (y * G + z) * this._texW + x;
          const s = this._pixBuf[pi * 4];

          this._displayScales[ci] += (this._targetScales[ci] - this._displayScales[ci]) * lerpT;
          if (this._displayScales[ci] < 0.02) continue;
          live++;

          dummy.position.set(x - off, y - off, z - off);
          dummy.scale.setScalar(this._displayScales[ci]);
          dummy.updateMatrix();

          if (Math.abs(s - S_NEWBORN) < 0.12) this._meshNewborn.setMatrixAt(cN++, dummy.matrix);
          else if (Math.abs(s - S_GROWING) < 0.12) this._meshGrowing.setMatrixAt(cG++, dummy.matrix);
          else if (Math.abs(s - S_STABLE) < 0.12) this._meshStable.setMatrixAt(cS++, dummy.matrix);
          else if (Math.abs(s - S_DYING) < 0.12) this._meshDying.setMatrixAt(cD++, dummy.matrix);
          else this._meshDying.setMatrixAt(cD++, dummy.matrix);
        }

    this._meshNewborn.count = cN;
    this._meshGrowing.count = cG;
    this._meshStable.count = cS;
    this._meshDying.count = cD;
    for (const m of [this._meshNewborn, this._meshGrowing, this._meshStable, this._meshDying]) {
      m.instanceMatrix.needsUpdate = true;
    }

    if (this._reseedCooldown > 0) this._reseedCooldown--;
    if (live < DEFAULTS.RESEED_THRESH && this._reseedCooldown === 0) this._mergeReseed();
    return live;
  }

  /**
   * @param {number} dt
   * @param {Record<string, number>} resolved
   */
  update(dt, resolved = {}) {
    this._lastDt = dt;
    this._controls?.update();

    if (!this._gl || !this._meshNewborn) return;

    let genInterval = Math.max(1, Math.round(Number(this._params.genInterval) || DEFAULTS.GEN_INTERVAL));
    if (resolved.caSpeed !== undefined) {
      genInterval = Math.max(1, Math.round(12 - clamp01(resolved.caSpeed) * 11));
    }

    this._frame++;
    if (this._frame % genInterval === 0) {
      this._computeStep(this._gen);
      this._gen++;
    }

    const live = this._readbackAndDisplay();

    if (resolved.dirLightIntensity !== undefined && this._directionalLight) {
      const manual = Number(this._params.dirLightIntensity);
      const v = mapBind01(resolved, 'dirLightIntensity', 0, 12, Number.isFinite(manual) ? manual : 3);
      this._directionalLight.intensity = v;
    }
    if (resolved.pointLightIntensity !== undefined && this._pointLight) {
      const manual = Number(this._params.pointLightIntensity);
      const v = mapBind01(
        resolved,
        'pointLightIntensity',
        0,
        12,
        Number.isFinite(manual) ? manual : 3
      );
      this._pointLight.intensity = v;
    }
    if (resolved.ambientIntensity !== undefined && this._ambientLight) {
      const manual = Number(this._params.ambientIntensity);
      const v = mapBind01(resolved, 'ambientIntensity', 0, 12, Number.isFinite(manual) ? manual : 3);
      this._ambientLight.intensity = v;
    }
    if (resolved.pointLightPosZ !== undefined && this._pointLight) {
      const baseZ = Number(this._params.pointLightPosZ);
      const manualZ = Number.isFinite(baseZ) ? baseZ : 100;
      const pz = mapBind01(resolved, 'pointLightPosZ', -400, 600, manualZ);
      this._pointLight.position.z = pz * CA_POINT_LIGHT_POS_SCALE;
    }
    if (resolved.cameraFov !== undefined && this._camera) {
      const manual = Number(this._params.cameraFov);
      const camFov = mapBind01(resolved, 'cameraFov', 20, 90, Number.isFinite(manual) ? manual : 45);
      this._camera.fov = THREE.MathUtils.clamp(camFov, 10, 120);
      this._camera.updateProjectionMatrix();
    }

    void live;
  }

  /**
   * @param {number} w
   * @param {number} h
   */
  resize(w, h) {
    if (this._camera) {
      this._camera.aspect = w / Math.max(h, 1);
      this._camera.updateProjectionMatrix();
    }
  }

  dispose() {
    this._controls?.dispose();
    this._controls = null;

    for (const m of [this._meshNewborn, this._meshGrowing, this._meshStable, this._meshDying]) {
      if (m) this.threeScene.remove(m);
    }
    if (this._sharedBoxGeo) {
      this._sharedBoxGeo.dispose();
      this._sharedBoxGeo = null;
    }
    this._meshNewborn = null;
    this._meshGrowing = null;
    this._meshStable = null;
    this._meshDying = null;

    if (this._sharedBoxGeo) {
      this._sharedBoxGeo.dispose();
      this._sharedBoxGeo = null;
    }

    if (this._materials) {
      for (const k of Object.keys(this._materials)) {
        this._materials[k]?.dispose?.();
      }
      this._materials = null;
    }

    this._rtA?.dispose();
    this._rtB?.dispose();
    this._rtA = null;
    this._rtB = null;

    this._caQuad?.geometry.dispose();
    this._caQuad?.material.dispose();
    this._caQuad = null;
    this._caScene = null;
    this._caCam = null;

    this.threeScene.clear();

    this._gl = null;
    this._camera = null;
    this._canvas = null;
    this._ambientLight = null;
    this._directionalLight = null;
    this._pointLight = null;
    this._inited = false;
    this._frame = 0;
    this._gen = 0;
  }
}
